import { ChevronsDownUp, ChevronsUpDown, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { isCaptureMode } from '../lib/capture-mode';

export interface FloatingRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface FloatingWindowProps {
  id: string;
  title: string;
  icon: ReactNode;
  defaultRect: FloatingRect;
  minWidth?: number;
  minHeight?: number;
  className?: string;
  placement?: 'free' | 'bottom-center';
  toolbar?: ReactNode;
  forcedCollapsed?: boolean;
  emphasized?: boolean;
  children: ReactNode;
}

type DockCorner = 'top-left' | 'top-right' | 'bottom-right' | 'bottom-left';

const storagePrefix = 'vault-kosmos-window:v5:';
const collapsedMinWidth = 204;
const collapsedHeight = 44;
const edgePadding = 12;
const topSafeY = 76;
const snapDistance = 22;
let topZIndex = 40;

const dockCornerById: Record<string, DockCorner> = {
  controls: 'top-left',
  details: 'top-right',
  orientation: 'bottom-right',
  timeline: 'bottom-left'
};

export function FloatingWindow({
  id,
  title,
  icon,
  defaultRect,
  minWidth = 220,
  minHeight = 120,
  className,
  placement = 'free',
  toolbar,
  forcedCollapsed,
  emphasized = false,
  children
}: FloatingWindowProps) {
  const storageKey = `${storagePrefix}${id}`;
  const captureMode = isCaptureMode();
  const [rect, setRect] = useState(() => (captureMode ? clampRect(defaultRect, minWidth, minHeight) : loadRect(storageKey, defaultRect)));
  const [collapsed, setCollapsed] = useState(() => (captureMode ? false : loadCollapsed(storageKey)));
  const [zIndex, setZIndex] = useState(24);
  const [viewportTick, setViewportTick] = useState(0);
  const effectiveCollapsed = forcedCollapsed ?? collapsed;
  const collapsedWidth = collapsedTabWidth(title);
  const dockCorner = dockCornerForWindow(id);
  const dragStart = useRef<{ pointerX: number; pointerY: number; rect: FloatingRect } | undefined>(undefined);
  const resizeStart = useRef<{ pointerX: number; pointerY: number; rect: FloatingRect } | undefined>(undefined);

  useEffect(() => {
    if (captureMode) return;
    window.localStorage.setItem(storageKey, JSON.stringify({ rect, collapsed }));
  }, [captureMode, collapsed, rect, storageKey]);

  useEffect(() => {
    const clampOnResize = () => {
      setRect((current) => clampRect(current, minWidth, minHeight));
      setViewportTick((value) => value + 1);
    };
    window.addEventListener('resize', clampOnResize);
    return () => window.removeEventListener('resize', clampOnResize);
  }, [minHeight, minWidth]);

  const style = useMemo(() => {
    if (placement === 'bottom-center') {
      return centeredBottomRect(effectiveCollapsed, collapsedWidth, collapsedHeight, rect, zIndex) as CSSProperties;
    }

    const tabRect = collapsedTabRect(dockCorner, collapsedWidth);
    return {
      left: effectiveCollapsed ? tabRect.x : rect.x,
      top: effectiveCollapsed ? tabRect.y : rect.y,
      width: effectiveCollapsed ? collapsedWidth : rect.width,
      height: effectiveCollapsed ? collapsedHeight : rect.height,
      zIndex
    } as CSSProperties;
  }, [collapsedWidth, dockCorner, effectiveCollapsed, placement, rect, viewportTick, zIndex]);

  function bringToFront() {
    topZIndex += 1;
    setZIndex(topZIndex);
  }

  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest('button, input, select, textarea, a')) return;
    if (effectiveCollapsed) return;
    if (placement === 'bottom-center') return;
    bringToFront();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      rect
    };
  }

  function drag(event: React.PointerEvent<HTMLDivElement>) {
    if (placement === 'bottom-center') return;
    if (!dragStart.current) return;
    const deltaX = event.clientX - dragStart.current.pointerX;
    const deltaY = event.clientY - dragStart.current.pointerY;
    const next = {
      ...dragStart.current.rect,
      x: dragStart.current.rect.x + deltaX,
      y: dragStart.current.rect.y + deltaY
    };
    setRect(clampRect(next, minWidth, minHeight));
  }

  function stopDrag(event: React.PointerEvent<HTMLDivElement>) {
    if (dragStart.current) {
      setRect((current) => snapRectToEdges(clampRect(current, minWidth, minHeight), minWidth, minHeight));
    }
    dragStart.current = undefined;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer capture may already be released when the pointer leaves the window.
    }
  }

  function startResize(event: React.PointerEvent<HTMLButtonElement>) {
    if (placement === 'bottom-center') return;
    event.stopPropagation();
    bringToFront();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStart.current = { pointerX: event.clientX, pointerY: event.clientY, rect };
  }

  function resize(event: React.PointerEvent<HTMLButtonElement>) {
    if (placement === 'bottom-center') return;
    if (!resizeStart.current) return;
    const deltaX = event.clientX - resizeStart.current.pointerX;
    const deltaY = event.clientY - resizeStart.current.pointerY;
    const next = {
      ...resizeStart.current.rect,
      width: Math.max(minWidth, resizeStart.current.rect.width + deltaX),
      height: Math.max(minHeight, resizeStart.current.rect.height + deltaY)
    };
    setRect(clampRect(next, minWidth, minHeight));
  }

  function stopResize(event: React.PointerEvent<HTMLButtonElement>) {
    if (resizeStart.current) {
      setRect((current) => snapRectToEdges(clampRect(current, minWidth, minHeight), minWidth, minHeight));
    }
    resizeStart.current = undefined;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer capture may already be released when the pointer leaves the window.
    }
  }

  function resetWindow() {
    setRect(clampRect(defaultRect, minWidth, minHeight));
    setCollapsed(false);
  }

  function toggleCollapsed() {
    bringToFront();
    if (forcedCollapsed !== undefined) return;
    setCollapsed((value) => !value);
  }

  return (
    <section
      className={['floating-window', effectiveCollapsed ? 'is-collapsed' : '', emphasized ? 'is-emphasized' : '', className].filter(Boolean).join(' ')}
      style={style}
      data-window-id={id}
      data-dock-corner={placement === 'bottom-center' ? 'bottom-center' : dockCorner}
      aria-label={title}
      onPointerDown={bringToFront}
    >
      <div
        className="floating-window__bar"
        onPointerDown={startDrag}
        onPointerMove={drag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
      >
        <span className="floating-window__title">
          {icon}
          <strong>{title}</strong>
        </span>
        <div className="floating-window__tools">
          {!effectiveCollapsed && toolbar}
          <button type="button" onClick={toggleCollapsed} title={effectiveCollapsed ? `${title} öffnen` : `${title} einklappen`}>
            {effectiveCollapsed ? <ChevronsUpDown size={14} /> : <ChevronsDownUp size={14} />}
          </button>
          <button type="button" onClick={resetWindow} title={`${title} zurücksetzen`}>
            <RotateCcw size={14} />
          </button>
        </div>
      </div>
      {!effectiveCollapsed && <div className="floating-window__body">{children}</div>}
      {!effectiveCollapsed && placement === 'free' && (
        <button
          className="floating-window__resize"
          type="button"
          aria-label={`${title} Größe ändern`}
          onPointerDown={startResize}
          onPointerMove={resize}
          onPointerUp={stopResize}
          onPointerCancel={stopResize}
        />
      )}
    </section>
  );
}

function collapsedTabWidth(title: string): number {
  return Math.max(collapsedMinWidth, Math.min(252, 128 + title.length * 8));
}

function dockCornerForWindow(id: string): DockCorner {
  return dockCornerById[id] ?? 'top-left';
}

function collapsedTabRect(corner: DockCorner, width: number): { x: number; y: number } {
  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;

  if (corner === 'top-right') {
    return { x: viewportWidth - width - edgePadding, y: topSafeY };
  }

  if (corner === 'bottom-right') {
    return { x: viewportWidth - width - edgePadding, y: viewportHeight - collapsedHeight - edgePadding };
  }

  if (corner === 'bottom-left') {
    return { x: edgePadding, y: viewportHeight - collapsedHeight - edgePadding };
  }

  return { x: edgePadding, y: topSafeY };
}

function centeredBottomRect(
  collapsed: boolean,
  collapsedWidth: number,
  collapsedHeight: number,
  rect: FloatingRect,
  zIndex: number
): CSSProperties {
  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;
  const width = collapsed ? collapsedWidth : Math.min(rect.width, Math.max(280, viewportWidth - 24));
  const height = collapsed ? collapsedHeight : rect.height;
  return {
    left: Math.max(edgePadding, (viewportWidth - width) / 2),
    top: Math.max(topSafeY, viewportHeight - height - edgePadding),
    width,
    height,
    zIndex
  };
}

function loadRect(storageKey: string, fallback: FloatingRect): FloatingRect {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return clampRect(fallback, 160, 90);
    const parsed = JSON.parse(raw) as { rect?: FloatingRect };
    if (!parsed.rect) return clampRect(fallback, 160, 90);
    return clampRect(parsed.rect, 160, 90);
  } catch {
    return clampRect(fallback, 160, 90);
  }
}

function loadCollapsed(storageKey: string): boolean {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return false;
    return Boolean((JSON.parse(raw) as { collapsed?: boolean }).collapsed);
  } catch {
    return false;
  }
}

function clampRect(rect: FloatingRect, minWidth: number, minHeight: number): FloatingRect {
  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;
  const width = Math.min(Math.max(rect.width, minWidth), Math.max(minWidth, viewportWidth - 24));
  const height = Math.min(Math.max(rect.height, minHeight), Math.max(minHeight, viewportHeight - 88));
  return {
    x: Math.min(Math.max(edgePadding, rect.x), Math.max(edgePadding, viewportWidth - width - edgePadding)),
    y: Math.min(Math.max(topSafeY, rect.y), Math.max(topSafeY, viewportHeight - height - edgePadding)),
    width,
    height
  };
}

function snapRectToEdges(rect: FloatingRect, minWidth: number, minHeight: number): FloatingRect {
  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;
  const next = { ...rect };

  if (Math.abs(next.x - edgePadding) <= snapDistance) next.x = edgePadding;
  if (Math.abs(viewportWidth - (next.x + next.width) - edgePadding) <= snapDistance) {
    next.x = viewportWidth - next.width - edgePadding;
  }
  if (Math.abs(next.y - topSafeY) <= snapDistance) next.y = topSafeY;
  if (Math.abs(viewportHeight - (next.y + next.height) - edgePadding) <= snapDistance) {
    next.y = viewportHeight - next.height - edgePadding;
  }

  return clampRect(next, minWidth, minHeight);
}
